import { useEffect, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Alert, Table, TableBody, TableContainer } from '@mui/material';
import TablePagination from '../../../../theme/overrides/TablePagination';
import { ListHead } from '../../../organisms';
import { scroll } from '../../../../utils/scroll';
import LoadingScreen from '../../../loadings/LoadingScreen';
import DocumentsRows from './DocumentsRows';
import CancelDemandModal from '../../demands/CancelDemandModal';
import useAuth from '../../../../hooks/useAuth';
import useDemands from '../../demands/hook/useDemands';
import { applyFilter } from './helpers/documentsTableHelpers';
import { getCompanies } from '../../../../redux/slices/companies';
import { updateUserDocumentStatus } from '../../../../redux/slices/files';
import { useDispatch, useSelector } from '../../../../redux/store';
import moment from 'moment';
import DocumentValidationModal from './DocumentValidationModal';
import { useSnackbar } from 'notistack';
import CloseButton from '../../../features/clockings/ClockingButtons/ClockingButton/CloseButton';

const tableHead = [
  { id: 'filler', label: '', mHidden: 'mdDown' },
  { align: 'left', id: 'filename', label: 'Document', mHidden: 'mdDown' },
  { align: 'left', id: 'type', label: 'Type', mHidden: 'mdDown' }
];

/**
 * Displays a table with documents, providing capabilities such as sorting, pagination,
 * and filtering, along with a detailed modal view for selected documents.
 *
 * @param {Object} props - Component props.
 * @param {Array} props.files - Array of document data.
 * @param {Object} props.filters - Filtering criteria for documents.
 * @param {boolean} props.inResourceAccordion - Specifies if the table is rendered within an accordion.
 * @param {boolean} props.isLoading - Loading state indicator.
 * @param {boolean} props.showHeadCols - Indicates if the table header should be displayed.
 * @param {boolean} props.showPagination - Indicates if pagination controls should be displayed.
 * @returns {JSX.Element} The rendered component.
 */

const DocumentsTable = (props) => {
  const {
    files = [],
    filters = {},
    handleReloadFiles,
    inResourceAccordion = false,
    isLoading = false,
    showHeadCols = false,
    showPagination = false,
    title,
    isSingleFile,
    handleCloseModal
  } = props;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const dispatch = useDispatch();
  const { isPartner, user } = useAuth();
  const { unseenFilesSiteCount, locationKey, unseenFilesSite } = useSelector((state) => state.files);

  const [selectedFile, setSelectedFile] = useState({});
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [sortBy, setSortBy] = useState('created_at');
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);

  const {
    handleCloseModalConfirmCancellation,
    handleConfirmDemandCancellation,
    handleOpenModalConfirmCancellation,
    openModalConfirmCancellation
  } = useDemands(handleReloadFiles);
  // Ce useEffect permet d'effectuer l'ouverture de la modale via la notification push
  useEffect(() => {
    const dataFileId = sessionStorage.getItem('fileToOpen');

    if (dataFileId && files?.length > 0) {
      const file = files.find((file) => Number(file.id) === Number(dataFileId));

      if (file) {
        setSelectedFile(file);
        setOpen(true);
        sessionStorage.removeItem('fileToOpen');
      }
    }
  }, [files, isSingleFile]);

  useEffect(() => {
    if (isSingleFile) {
      setSelectedFile(files[0]);
      setOpen(true);
    }
  }, []);

  const filteredFiles = Object.keys(filters).length
    ? applyFilter(tableHead, files, filters, sortBy, order, isPartner)
    : files;

  if (page > 0 && !showPagination) {
    setPage(0);
  }

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
    scroll();
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = useCallback(
    (_, property) => {
      const isAsc = sortBy === property && order === 'asc';
      setOrder(isAsc ? 'desc' : 'asc');
      setSortBy(property);
    },
    [sortBy, order]
  );

  const handleClickFile = (item) => {
    setSelectedFile(item);
    setOpen(true);
  };

  const handleConfirm = (e) => {
    const demandId = isPartner ? selectedFile.partner_demand_id : selectedFile.id;
    const fileId = isPartner ? selectedFile.partner_file_demand_id : selectedFile.file_id;
    handleConfirmDemandCancellation(e, demandId, fileId);
  };

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!filteredFiles?.length) {
    return (
      <Alert severity="info" sx={{ m: inResourceAccordion ? 2 : null }}>
        Aucun document
      </Alert>
    );
  }

  const handleUpdateDocumentNotificationStatus = () => {
    if (selectedFile.viewed_at === null) {
      const isValidationRequired = selectedFile.validated_at === null;
      const fileKeyword = selectedFile.type;

      dispatch(
        updateUserDocumentStatus(
          {
            selectedFileId: selectedFile.id,
            timestamp: moment().format('YYYY-MM-DD HH:mm:ss'),
            viewedAtUpdate: true,
            validatedAtUpdate: false,
            isValidationRequired,
            fileKeyword
          },

          (message, result) => {
            const updates = {
              viewed_at:
                selectedFile.viewed_at === null ? moment().format('YYYY-MM-DD HH:mm:ss') : selectedFile.viewed_at
            };

            if (fileKeyword === 'Société') {
              dispatch(getCompanies({ companyId: isPartner ? user.userSubCompanyId : null }));
            }

            setSelectedFile((prevState) => ({
              ...prevState,
              ...updates
            }));
            if (!result.success) {
              console.error(message, result);
            }
          }
        )
      );
    }
  };

  const handleValidateUserDocument = () => {
    setLoading(true);

    dispatch(
      updateUserDocumentStatus(
        {
          selectedFileId: selectedFile.id,
          timestamp: moment().format('YYYY-MM-DD HH:mm:ss'),
          viewedAtUpdate: selectedFile.viewed_at === null,
          validatedAtUpdate: true
        },
        (message, result) => {
          const updates = {
            viewed_at:
              selectedFile.viewed_at === null ? moment().format('YYYY-MM-DD HH:mm:ss') : selectedFile.viewed_at,
            validated_at: moment().format('YYYY-MM-DD HH:mm:ss')
          };
          setSelectedFile((prevState) => ({
            ...prevState,
            ...updates
          }));
          setLoading(false);

          if (!result.success) {
            console.error(message, result);
          }

          return enqueueSnackbar(message, {
            variant: result.success ? 'success' : 'error',
            action: (key) => <CloseButton handleClick={() => closeSnackbar(key)} />
          });
        }
      )
    );
  };

  return (
    <>
      <TableContainer>
        <Table
          id="table-documents"
          size="small"
          sx={{
            width: '100%',
            tableLayout: 'auto'
          }}
        >
          {showHeadCols && !!filteredFiles?.length && (
            <ListHead headLabel={tableHead} onRequestSort={handleRequestSort} sortBy={[sortBy]} sortOrders={[order]} />
          )}
          <TableBody>
            <DocumentsRows
              files={filteredFiles}
              handleClickFile={handleClickFile}
              handleOpenModalConfirmCancellation={handleOpenModalConfirmCancellation}
              page={page}
              rowsPerPage={rowsPerPage}
              setSelectedFile={setSelectedFile}
              showOwner={showHeadCols}
              showPagination={showPagination}
            />
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <TablePagination
          labelRowsPerPage="Par page"
          component="div"
          count={filteredFiles.length}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          page={page}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 100]}
        />
      )}

      {open && (
        <DocumentValidationModal
          file={selectedFile}
          fullScreen
          onClose={() => {
            if (
              (['sites-list-nav', 'planning-nav-alert-files'].includes(locationKey) && isSingleFile) ||
              ((locationKey == 'sites-list-nav' || locationKey == 'planning-nav-alert-files') &&
                unseenFilesSiteCount === 0) ||
              ((locationKey == 'sites-list-nav' || locationKey == 'planning-nav-alert-files') &&
                unseenFilesSiteCount === 1 &&
                unseenFilesSite.filter((file) => file.validated_at === null).length === 1 &&
                unseenFilesSite.filter((file) => file.viewed_at !== null).length === 0)
            ) {
              handleCloseModal();
              setOpen(false);
            } else {
              setOpen(false);
            }
          }}
          onConfirm={handleValidateUserDocument}
          open
          loading={loading}
          title={title || selectedFile.name}
          onDownloadFallback={handleUpdateDocumentNotificationStatus}
          onViewedFileCallback={handleUpdateDocumentNotificationStatus}
        />
      )}

      {openModalConfirmCancellation && (
        <CancelDemandModal
          handleClose={handleCloseModalConfirmCancellation}
          handleConfirm={handleConfirm}
          open={openModalConfirmCancellation}
        />
      )}
    </>
  );
};

DocumentsTable.propTypes = {
  files: PropTypes.array,
  filters: PropTypes.object,
  handleReloadFiles: PropTypes.func,
  inResourceAccordion: PropTypes.bool,
  isLoading: PropTypes.bool,
  showHeadCols: PropTypes.bool,
  showPagination: PropTypes.bool,
  title: PropTypes.string,
  isSingleFile: PropTypes.bool,
  handleCloseModal: PropTypes.func
};

export default DocumentsTable;
